import { amgenCVOTStudy, ascvdEstablishedStudy, ascvdHighRiskStudy, azPandaFluStudy, cidara18HighRiskStudy, cidaraFluPreventionStudy, modcoVP401Study, moderna1273CovidStudy, mpoxVaccineStudy, novavaxCovid65Study, novavaxCovidBoosterStudy, seqirusFluVaccineStudy } from "../features/multi-study";
import {
  AffiliateDestination,
  LandingPageConfig,
  LandingPageTemplate,
  LandingPageWithVariants,
  LoginMethod,
  MultiStudyLandingPageConfig,
} from "../types/landingPage";
import { developmentLandingPageConfigs } from "./developmentLandingPageConfigs";
import { amgenCVDRiskScreeningQuestions } from "./pre-screener-config/AmgenCVDRiskScreener";
import { azAppreciateScreeningQuestions } from "./pre-screener-config/AZAppreciateScreener";
import { azAppreciateFlowBScreeningQuestions } from "./pre-screener-config/AZAppreciateScreenerFlowB";
import { azAppreciateFlowCScreeningQuestions } from "./pre-screener-config/AZAppreciateScreenerFlowC";
import { azascvdEstablishedScreeningQuestions } from "./pre-screener-config/AZASCVDEstablishedScreener";
import { azascvdHighRiskScreeningQuestions } from "./pre-screener-config/AZASCVDHighRiskScreener";
import { azPandaScreeningQuestions } from "./pre-screener-config/AZPandaScreener";
import { cidara18HighRiskScreeningQuestions } from "./pre-screener-config/Cidara18HighRiskScreener";
import { cidaraFluVaxScreeningQuestions } from "./pre-screener-config/CidaraFluVaxScreener";
import { curraxScreeningQuestions } from "./pre-screener-config/CurraxScreener";
import { heartFailureRiskScreeningQuestions } from "./pre-screener-config/HeartFailureRiskScreener";
import { ldlScreeningQuestions } from "./pre-screener-config/LDLScreener";
import { legPainScreeningQuestions, legPainTrialRouting } from "./pre-screener-config/LegPainScreener";
import { legPainScreeningQuestionsB } from "./pre-screener-config/LegPainScreenerB";
import { levanteAsthmaScreeningQuestions } from "./pre-screener-config/LevanteAsthmaScreener";
import { lpTestScreeningQuestions } from "./pre-screener-config/LpTestScreener";
import { mildAsthmaScreeningQuestions } from "./pre-screener-config/MildAsthmaScreener";
import { moderna1273ScreeningQuestions } from "./pre-screener-config/Moderna1273Screener";
import { covid50to64HealthyScreeningQuestions, covidVaccineTrialRouting } from "./pre-screener-config/COVID50to64HealthyScreener";
import { covid50to64SimplifiedScreeningQuestions } from "./pre-screener-config/COVID50to64HealthyScreenerSimplified";
import { covid50to64HealthyScreeningQuestionsD } from "./pre-screener-config/COVID50to64HealthyScreenerD";
import { mpoxVaccineScreeningQuestions } from "./pre-screener-config/MpoxVaccineScreener";
import { norovirusVaccineScreeningQuestions } from "./pre-screener-config/NorovirusVaccineScreener";
import { mylanContraceptivePatchScreeningQuestions } from "./pre-screener-config/MylanContraceptivePatch";
import { novavaxBoosterScreeningQuestions } from "./pre-screener-config/NovavaxBoosterScreener";
import { novavaxCovid65ScreeningQuestions } from "./pre-screener-config/NovavaxCovid65Screener";
import { prestoCOPDhmioScreeningQuestions } from "./pre-screener-config/PrestoCOPDhmioScreener";
import { prestoCOPDhmioScreenerCQuestions } from "./pre-screener-config/PrestoCOPDhmioScreenerC";
import { sequirisFluVaccineScreeningQuestions } from "./pre-screener-config/SequirisFluVaccineScreener";
import { sharedASCVDScreeningQuestions } from "./pre-screener-config/sharedASCVDScreenerQuestions";
import { sharedVaccineScreeningQuestions } from "./pre-screener-config/sharedVaccineScreenerQuestions";
import { sparrowDiabetesScreeningQuestions } from "./pre-screener-config/SparrowDiabetesScreener";
import { sparrowDiabetesScreeningQuestionsC } from "./pre-screener-config/SparrowDiabetesScreenerC";
import { tharrosCOPDScreeningQuestions } from "./pre-screener-config/TharrosCOPDScreener";
import { vaccineTest1ScreeningQuestions } from "./pre-screener-config/VaccineTest1Screener";
import { vikingT2dScreeningQuestions } from "./pre-screener-config/VikingT2d";
import { vikingT2dScreeningQuestionsAI } from "./pre-screener-config/VikingT2dScreenerAI";
import { alnylamHypertensionScreeningQuestions } from "./pre-screener-config/AlnylamHypertensionScreener";

const isDev = import.meta.env.DEV;

// Production configurations
export const productionConfigs: Record<string, LandingPageWithVariants | MultiStudyLandingPageConfig> = {
  
  // Viking T2D
  vt2d: {
    id: "vt2d",
    template: LandingPageTemplate.EXTENDED,
    title:
      "Type 2 Diabetes Weight Management Study | HealthMatch",
    description:
      "Join a clinical trial testing an investigational medication for adults with Type 2 Diabetes. Study-related compensation for time and travel is available if eligible.",
    heroTitle: "Investigational Weight Management Study for Adults with Type 2 Diabetes",
    heroSubtitle: "",
    introText: "Compensation may be available.",
    longDescription:
      "This study is testing the safety and effectiveness of an investigational medication to help adults with type 2 diabetes who are overweight or obese manage their weight.\n\nThe study will assess how well the medication helps reduce body weight and improve related health indicators.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: vikingT2dScreeningQuestions,
    conditionId: 523,
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VOVERVK2735302",
    countryCode: "US",
    status: "active",
    variants: {
      "vt2d-b": {
        id: "vt2d-b",
        masterId: "vt2d",
        status: "active",
        preScreeningQuestions: vikingT2dScreeningQuestionsAI,
      },
    },
  },

  // Sparrow Diabetes - Type 2 Diabetes with Elevated Cortisol
  "t2d-ec": {
    id: "t2d-ec",
    template: LandingPageTemplate.EXTENDED,
    title: "Type 2 Diabetes Study for Adults with Elevated Cortisol | HealthMatch",
    description:
      "Join a clinical trial studying an investigational medication for adults with hard-to-control type 2 diabetes. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "Investigational Study for Adults with Hard-to-Control Type 2 Diabetes",
    heroSubtitle: "",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objectives of this study are to evaluate the safety and effectiveness of an investigational medication in adults with type 2 diabetes who may have elevated cortisol levels, which can make managing blood sugar more difficult.\n\nThe study will compare different doses of the investigational medication to a placebo to better understand its effects on blood sugar control and other health markers, while participants continue taking their regular diabetes medications during the study.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: sparrowDiabetesScreeningQuestions,
    conditionId: 372,
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VDIABCL2006",
    countryCode: "US",
    status: "active",
    variants: {
      "t2d-ec-c": {
        id: "t2d-ec-c",
        masterId: "t2d-ec",
        status: "testing",
        preScreeningQuestions: sparrowDiabetesScreeningQuestionsC,
      },
    },
  },

  // Vertext DPN - Lower Leg Pain Study
  legpain: {
    id: "legpain",
    template: LandingPageTemplate.EXTENDED,
    title: "Lower Leg Pain Management Study | HealthMatch",
    description:
      "Join a clinical trial testing an investigational medication for adults with lower leg pain. Study-related compensation for time and travel is available if eligible.",
    heroTitle: "Studying an Investigational Medication to Manage Pain",
    heroSubtitle: "",
    introText: "Compensation may be available.",
    longDescription:
      "The main purpose of this study is to assess the effectiveness and safety of an investigational medication for adults experiencing pain caused by diabetes.\n\nParticipants will be randomly assigned to receive either the investigational medication, a placebo, or a currently available medication. The study compares how well each option helps reduce daily pain levels.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.\n\nExpressing interest does not commit you to participating in the study; it simply allows you to learn more about it from the research site before making an informed decision.",
    preScreeningQuestions: legPainScreeningQuestions,
    conditionId: 437,
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: {
      conditionalRouting: legPainTrialRouting,
    },
    countryCode: "US",
    status: "active",
    variants: {
      "legpain-b": {
        id: "legpain-b",
        masterId: "legpain",
        status: "active",
        preScreeningQuestions: legPainScreeningQuestionsB,
      },
    },
  },

  // LP Test Study
  "lp-test": {
    id: "lp-test",
    template: LandingPageTemplate.EXTENDED,
    title: "Internal Engineering Test Page | HealthMatch",
    description:
      "This is an internal engineering test landing page. Not a real clinical trial.",
    heroTitle: "Engineering Test Page",
    heroSubtitle: "For Internal Testing Purposes Only",
    introText: "This page is not a real clinical trial.",
    longDescription:
      "This is an internal engineering test page and does not represent a real clinical trial. This configuration is used for testing the landing page functionality and infrastructure. \n\nAny data submitted through this page is for testing purposes only and will not be processed as a real trial application.",
    preScreeningQuestions: lpTestScreeningQuestions,
    conditionId: 320,
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "LPTEST",
    countryCode: "US",
    status: "testing",
    affiliateDestinations: [
      AffiliateDestination.TEST_DESTINATION,
      AffiliateDestination.TEST_DESTINATION_2,
    ],
  },

  // Vaccine Test 1 - Simple test landing page
  "vaccine-test-1": {
    id: "vaccine-test-1",
    template: LandingPageTemplate.EXTENDED,
    title: "Vaccine Test Page | HealthMatch",
    description:
      "This is a test landing page for vaccine studies. Not a real clinical trial.",
    heroTitle: "Vaccine Test Page",
    heroSubtitle: "For Testing Purposes Only",
    introText: "This is a test page for vaccine-related studies.",
    longDescription:
      "This is a test page that demonstrates vaccine study screening. It asks participants about recent vaccine history.\n\nAny data submitted through this page is for testing purposes only and will not be processed as a real trial application.",
    preScreeningQuestions: vaccineTest1ScreeningQuestions,
    conditionId: 97, // Healthy volunteers
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VACCINE_TEST_1",
    countryCode: "US",
    status: "testing",
  },

  // Mild Asthma Study
  "mild-asthma": {
    id: "mild-asthma",
    template: LandingPageTemplate.EXTENDED,
    title: "Asthma Inhaler Clinical Trial | HealthMatch",
    description:
      "Join a clinical trial for people with asthma. Compensation for time and travel may be available for eligible participants.",
    heroTitle: "For Adults with Mild Asthma",
    heroSubtitle:
      "Studying a More Environmentally Friendly Asthma Inhaler",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "This study aims to confirm the safety and effectiveness of an asthma inhaler containing a new type of propellant, which is an ingredient used to deliver medication into the lungs. This new propellant has lower global warming potential than standard inhaler propellants.\n\nParticipants will be randomly assigned to receive either the investigational inhaler or a currently approved asthma inhaler.\n\nShould you express interest, you will be contacted directly by the research site, who will provide further details and answer any questions you may have about study requirements, risks/benefits, and any compensation.",
    preScreeningQuestions: mildAsthmaScreeningQuestions,
    conditionId: 130, // Asthma condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VASTHC00002",
    countryCode: "US",
    status: "inactive",
  },

  // Levante Asthma Study (internal reference)
  "mod-asthma": {
    id: "mod-asthma",
    template: LandingPageTemplate.EXTENDED,
    title: "Asthma Clinical Research Study | HealthMatch",
    description:
      "Join a clinical trial for adults with asthma who experience symptoms despite current treatment. Compensation may be available for eligible participants.",
    heroTitle: "For Adults with Asthma",
    heroSubtitle:
      "Studying Investigational Medications for Improving Asthma Control",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "These studies aim to evaluate the safety and effectiveness of investigational medications in adults with uncontrolled asthma who are at risk of exacerbations despite using combination preventer inhalers (ICS/LABA).\n\nParticipants will be randomly assigned to receive the investigational medication or a placebo along with their current asthma treatment.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: levanteAsthmaScreeningQuestions,
    conditionId: 130, // Asthma condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VASTH6830C00003",
    countryCode: "US",
    status: "inactive",
  },

  // Tharros COPD Study (internal reference)
  "copd-cv": {
    id: "copd-cv",
    template: LandingPageTemplate.EXTENDED,
    title: "COPD Clinical Research Study | HealthMatch",
    description:
      "Join a groundbreaking clinical trial for COPD. We're seeking current or former smokers with COPD who have cardiovascular risk factors. Compensation may be provided.",
    heroTitle: "Investigational COPD Study for Adults",
    heroSubtitle: "",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main goals of this study are to assess the safety and immune response of an investigational medication treatment in adults with COPD who are not currently receiving Inhaled Corticosteroid (ICS) and are at a high risk of heart and lung-related outcomes.\n\nShould you express interest, you will be contacted directly by the research site, who will provide further details and answer any questions you may have about study requirements, risks/benefits, and any compensation.",
    preScreeningQuestions: tharrosCOPDScreeningQuestions,
    conditionId: 208, // COPD condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VCOPD0001", 
    countryCode: "US",
    status: "active",
  },

  // Mylan Contraceptive Patch Study
  "contraceptive-patch": {
    id: "contraceptive-patch",
    template: LandingPageTemplate.EXTENDED,
    title: "Contraceptive Patch Clinical Trial | HealthMatch",
    description:
      "Join a clinical trial for women testing an investigational contraceptive patch. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Women Seeking Contraception",
    heroSubtitle:
      "Studying an Investigational Hormonal Contraceptive Patch",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "This study aims to evaluate the safety and effectiveness of an investigational hormonal patch for contraception in people assigned female at birth, who have a BMI of 30 or higher.\n\nParticipants will use the study patch as their only method of contraception for approximately 12 months.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: mylanContraceptivePatchScreeningQuestions,
    
    conditionId: 97, // Using healthy condition ID as it's a contraceptive study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: {
      ageCohorts: [
        {
          minAgeInclusive: 16,
          maxAgeExclusive: 35,
          trialId: "VFERT130A01TD3001",
        },
      ],
    },
    countryCode: "US",
    status: "inactive",
  },

  // LDL Cholesterol Management Study
  "ldl-study": {
    id: "ldl-study",
    template: LandingPageTemplate.EXTENDED,
    title: "Cholesterol Management Study | HealthMatch",
    description:
      "Join a clinical trial testing an investigational medication for adults with high LDL cholesterol who are already on treatment. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults on Treatment for High Cholesterol",
    heroSubtitle:
      "Studying an Investigational Medication for Cholesterol Management",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "This study aims to evaluate the safety and effectiveness of an investigational medication for managing LDL cholesterol ('bad cholesterol') in adults with cardiovascular disease or cardiovascular risk factors who are already on treatment for their cholesterol.\n\nParticipants will receive either the investigational medication or a placebo in addition to their current cholesterol-lowering therapy. The study will assess how well the medication helps reduce LDL cholesterol levels over approximately 2 years.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: ldlScreeningQuestions,
    conditionId: 213, // High Cholesterol condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "EVLDLC000012",
    countryCode: "US",
    status: "active",
  },
  
  // Amgen CVD Risk Study
  "cvd-risk": {
    id: "cvd-risk",
    template: LandingPageTemplate.EXTENDED,
    title: "Cardiovascular Disease Risk Management Study | HealthMatch",
    description:
      "Join a clinical trial for adults with cardiovascular disease. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults with Cardiovascular Disease",
    heroSubtitle: "Studying an Investigational Medication for Cardiovascular Disease Risk Reduction",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "This study is investigating the efficacy of an investigational medication for adults who are overweight or obese and have a history of cardiovascular disease.\n\nParticipants will receive either the investigational medication or a placebo.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: amgenCVDRiskScreeningQuestions,
    conditionId: 362, // Using cardiovascular condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VCVDAMG196",
    countryCode: "US",
    status: "active",
  },

  // Currax Study
  "cardiovascular-safety": {
    id: "cardiovascular-safety",
    template: LandingPageTemplate.EXTENDED,
    title: "Cardiovascular Safety Study | HealthMatch",
    description:
      "Join a clinical trial evaluating the cardiovascular benefits of an investigational medication for adults with obesity and increased risk of cardiovascular events, this includes participants with Type 2 Diabetes. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "Study Evaluating Heart Health Effects of a Weight Management Medication",
    heroSubtitle: "",
    introText: "Compensation may be available",
    longDescription:
      "The main purpose of the study is to evaluate the cardiovascular effects of a study medication compared with a placebo in participants at increased risk of adverse cardiovascular outcomes (e.g., heart attack and stroke).\n\nParticipants will be randomly assigned to receive either the study medication or a placebo, with safety monitored throughout.\n\nShould you express interest, you will be contacted directly by the research site, who will provide further details and answer any questions you may have about study requirements, risks/benefits, and any compensation.",
    preScreeningQuestions: curraxScreeningQuestions,
    conditionId: 523, // Cardiovascular condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VOVERVOT3",
    countryCode: "US",
    status: "active",
  },

  // AZ Appreciate Chronic Kidney Disease Study
  "apo-ckd": {
    id: "apo-ckd",
    template: LandingPageTemplate.EXTENDED,
    title: "Chronic Kidney Disease Study | HealthMatch",
    description:
      "For adults of African descent with Chronic Kidney Disease - studying an investigational treatment for APOL-1 related kidney disease. Study-related compensation for time and travel is available if eligible.",
    heroTitle: "For Adults of African Descent with Chronic Kidney Disease",
    heroSubtitle:
      "Evaluating an Investigational Treatment for Kidney Disease",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "This study is investigating an investigational treatment for chronic kidney disease caused by genetic factors that primarily affect people of African descent, specifically focusing on APOL-1 mediated kidney disease.\n\nThe study aims to evaluate the safety and effectiveness of the investigational medication in slowing the progression of kidney disease in eligible participants. Participants will be randomly chosen to receive either the investigational medication or a placebo. About 2 out of 3 participants will receive the study drug. After this part of the study, everyone will have the chance to receive the study drug in a later phase, as long as early results show it's safe and helpful.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: azAppreciateScreeningQuestions,
    conditionId: 400, // Chronic kidney disease condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: {
      ageCohorts: [
        {
          minAgeInclusive: 18,
          maxAgeExclusive: 71,
          trialId: "AZCKDD6800C00005",
        },
      ],
    },
    countryCode: "US",
    status: "inactive", // Study closed - traffic redirects to the CKD condition page
    inactiveRedirectPath: "/cond/382",
    requireEmrConsent: true,
    variants: {
      "apo-ckd-b": {
        id: "apo-ckd-b",
        masterId: "apo-ckd",
        status: "inactive",
        heroSubtitle: "",
        longDescription: "",
        introText: "Compensation may be available",
        introTextClassName: "text-base",
        hideProgressBarOnFirstQuestion: true,
        hideProgressBarQuestionCount: true,
        preScreeningQuestions: azAppreciateFlowBScreeningQuestions,
      },
      "apo-ckd-c": {
        id: "apo-ckd-c",
        masterId: "apo-ckd",
        status: "inactive",
        preScreeningQuestions: azAppreciateFlowCScreeningQuestions,
      },
    },
  },

  // MJFF Smell Test diagnosed PD (MJFFPDSNS2) (MJFF - direct redirect)
  "ppmi-dpd-r": {
    id: "ppmi-dpd-r",
    template: LandingPageTemplate.EXTENDED,
    title: "Parkinson's Disease Research Study | HealthMatch",
    description:
      "Join a long-term observational research study for adults with Parkinson's disease. Help advance research and contribute to a better understanding of Parkinson's disease.",
    heroTitle: "For Adults with Parkinson's Disease",
    heroSubtitle:
      "Contribute to Groundbreaking Parkinson's Research",
    introText: "Help advance research into Parkinson's disease.",
    longDescription:
      "If you are interested in this study, you'll be directed to a secure partner website to complete the next step of pre-screening and to apply.\n\nThe Parkinson's Progression Markers Initiative (PPMI) is a landmark observational study sponsored by The Michael J. Fox Foundation. Its goal is to identify markers of Parkinson's disease progression to accelerate the development of new treatments.\n\nParticipants help advance research by contributing data over time through clinic visits, assessments, and biological samples. PPMI is open to people with Parkinson's disease as well as those at risk and healthy volunteers.",
    conditionId: 99, // Parkinson's disease condition ID
    countryCode: "US",
    status: "active",
    directRedirect: true,
    defaultCampaign: "pd_direct",
    qualifiedRedirectMessage: "We're directing you to the study website to complete your application.",
    externalRedirectUrl: "https://mysmelltest.org/healthmatch_web?partnerid=hm_$redirectToken&utm_source=healthmatch&utm_medium=app&utm_campaign=hm_lp_$redirectToken&utm_content=PPMI_questionnaire",
  },

  // COPD Study (Presto - direct redirect to trialandyou.com)
  "copd-r": {
    id: "copd-r",
    template: LandingPageTemplate.EXTENDED,
    title: "COPD Clinical Research Study | HealthMatch",
    description:
      "Join a clinical trial for adults with COPD. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults with COPD",
    heroSubtitle:
      "Studying an Investigational Treatment for COPD",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "If you are interested in this study, you'll be directed to a secure partner website to complete the next step of pre-screening and to apply.\n\nThe main purpose of this study is to assess the safety and effectiveness of an investigational medication in adults aged 40 and older who have moderate to very severe chronic obstructive pulmonary disease (COPD).\n\nParticipants will continue their usual COPD treatment and be randomly assigned to receive either the investigational medication or a placebo. The study will evaluate whether the investigational medication reduces the number and severity of COPD flare-ups, improves lung function, and helps with symptoms like breathlessness and cough.",
    conditionId: 208, // COPD condition ID
    countryCode: "US",
    status: "inactive", // Study paused
    directRedirect: true,
    defaultCampaign: "copd_direct",
    qualifiedRedirectMessage: "We're directing you to the study website to complete your application.",
    externalRedirectUrl: "https://trialandyou.com/ext/match?name=copd&utm_source=healthmatch&utm_medium=app&utm_campaign=hm_lp_$redirectToken&utm_content=COPD_questionnaire",
  },

  // COPD Study B (HMIO variant - traditional landing page)
  "copd-b": {
    id: "copd-b",
    template: LandingPageTemplate.EXTENDED,
    title: "COPD Clinical Research Study | HealthMatch",
    description:
      "Join a clinical trial for adults with COPD. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults with COPD",
    heroSubtitle:
      "Studying an Investigational Treatment for COPD",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main purpose of this study is to assess the safety and effectiveness of an investigational medication in adults aged 40 and older who have moderate to very severe chronic obstructive pulmonary disease (COPD).\n\nParticipants will continue their usual COPD treatment and be randomly assigned to receive either the investigational medication or a placebo.\n\nThe study will evaluate whether the investigational medication reduces the number and severity of COPD flare-ups, improves lung function, and helps with symptoms like breathlessness and cough.",
    preScreeningQuestions: prestoCOPDhmioScreeningQuestions,
    conditionId: 208, // COPD condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VCOPDD7860C00006",
    countryCode: "US",
    status: "inactive",
  },

  // COPD Study C (HMIO variant C - individual radio questions)
  "copd-c": {
    id: "copd-c",
    template: LandingPageTemplate.EXTENDED,
    title: "COPD Clinical Research Study | HealthMatch",
    description:
      "Join a clinical trial for adults with COPD. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults with COPD",
    heroSubtitle:
      "Studying an Investigational Treatment for COPD",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main purpose of this study is to assess the safety and effectiveness of an investigational medication in adults aged 40 and older who have moderate to very severe chronic obstructive pulmonary disease (COPD).\n\nParticipants will continue their usual COPD treatment and be randomly assigned to receive either the investigational medication or a placebo.\n\nThe study will evaluate whether the investigational medication reduces the number and severity of COPD flare-ups, improves lung function, and helps with symptoms like breathlessness and cough.",
    preScreeningQuestions: prestoCOPDhmioScreenerCQuestions,
    conditionId: 208, // COPD condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VCOPDD7860C00006",
    countryCode: "US",
    status: "inactive",
  },

  // Sequiris Influenza Vaccine Study
  "flu-vaccine-65-plus": {
    id: "flu-vaccine-65-plus",
    template: LandingPageTemplate.EXTENDED,
    title: "Flu Vaccine Clinical Trial for Adults 65+ | HealthMatch",
    description:
      "Join a clinical trial studying an investigational flu vaccine in adults aged 65 and older. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults 65 and Older",
    heroSubtitle:
      "Studying an Investigational Flu Vaccine",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objectives of this study are to evaluate the effectiveness, safety, and immune response of an investigational flu vaccine in adults aged 65 years and older.\n\nInfluenza, more commonly known as the flu, is a contagious respiratory illness that can lead to health complications, especially in older adults.\n\nParticipants will receive either the investigational vaccine or a currently available influenza vaccine.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: sequirisFluVaccineScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VFLUV11824",
    countryCode: "US",
    status: "inactive",
  },

  // Heart Failure Risk Study
  "heart-failure-risk": {
    id: "heart-failure-risk",
    template: LandingPageTemplate.EXTENDED,
    title: "Heart Failure Study | HealthMatch",
    description:
      "The main objectives of this study are to assess the effectiveness and safety of an investigational medication in adults with type 2 diabetes and cardiovascular disease who are at an increased risk of developing heart failure.",
    heroTitle: "For Adults at Risk of Heart Failure",
    heroSubtitle:
      "Studying an Investigational Medication for Adults with Type 2 Diabetes and Cardiovascular Disease",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objectives of this study are to assess the effectiveness and safety of an investigational medication in adults with type 2 diabetes and cardiovascular disease who are at increased risk of developing heart failure.\n\nParticipants will be randomly assigned to receive a currently available treatment, along with either the investigational medication or a placebo.\n\nThe study aims to evaluate whether the investigational medication can reduce the risk of heart failure events and deaths related to cardiovascular causes.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: heartFailureRiskScreeningQuestions,
    conditionId: 362, // Cardiovascular Disease condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: {
      ageCohorts: [
        {
          minAgeInclusive: 40,
          maxAgeExclusive: 100,
          trialId: "VT2DMD6973C00001",
        },
      ],
    },
    countryCode: "US",
    status: "active",
  },

  // Cidara Flu Vaccine Study (internal reference - legacy single-study)
  "healthy-65-flu-vax": {
    id: "healthy-65-flu-vax",
    template: LandingPageTemplate.EXTENDED,
    title: "Flu Vaccine Clinical Trial for Adults 65+ | HealthMatch",
    description:
      "Join a clinical trial studying an investigational flu preventative medication in adults aged 65 and older. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Healthy Adults 65 and Older",
    heroSubtitle:
      "Studying an Investigational Flu Prevention Medication",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objective of this study is to evaluate the effectiveness and safety of an investigational medication designed to prevent influenza in adults aged 65 and older.\n\nParticipants will be randomly assigned to receive either the investigational medication or a placebo via subcutaneous injection.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: cidaraFluVaxScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VFLUSQ30665",
    countryCode: "US",
    status: "inactive", // Closed - Replaced by multi-study vaccine-studies page
  },

  // Cidara 18+ High Risk Flu Prevention Study
  "flu-prevention-high-risk": {
    id: "flu-prevention-high-risk",
    template: LandingPageTemplate.EXTENDED,
    title: "Flu Prevention Study for Adults at Higher Risk | HealthMatch",
    description:
      "Join a clinical trial studying an investigational flu preventative medication for adults 18+ at higher risk of flu complications. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults at Higher Risk of Flu Complications",
    heroSubtitle:
      "Studying an Investigational Flu Prevention Treatment",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objective of this study is to evaluate the effectiveness and safety of an investigational medication designed to prevent influenza in adults who are at higher risk of developing complications from the flu.\n\nParticipants will be randomly assigned to receive either the investigational medication or a placebo via subcutaneous injection. The investigational medication is being studied as a potential one-time seasonal flu prevention option that does not rely on the immune system's response, making it especially relevant for those with chronic conditions or weakened immune systems.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: cidara18HighRiskScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VFLUSQ306",
    countryCode: "US",
    status: "inactive",
  },

  // Moderna 1273 COVID-19 Vaccine Study
  "covid-vaccine-65-moderna": {
    id: "covid-vaccine-65-moderna",
    template: LandingPageTemplate.EXTENDED,
    title: "COVID-19 Vaccine Study for Adults 65+ | HealthMatch",
    description:
      "Join a clinical trial studying an investigational COVID-19 vaccine designed to target newer variants. For adults 65+. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults 65 and Older",
    heroSubtitle:
      "Studying an Investigational COVID-19 Vaccine",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main purpose of this study is to assess the immune response and safety of an investigational COVID-19 vaccine designed to target newer variants of the virus.\n\nParticipants will receive a single dose of the investigational vaccine and will be monitored for any reactions and immune response over the following month.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: moderna1273ScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VMODCOV19P40365",
    countryCode: "US",
    status: "inactive",
  },

  // 3x COVID studies (Moderna, Novavax, Pfizer) for healthy adults 50-64
  "covid-vaccine-50-64": {
    id: "covid-vaccine-50-64",
    template: LandingPageTemplate.EXTENDED,
    title: "COVID-19 Investigational Vaccine Studies | HealthMatch",
    description:
      "Multiple COVID-19 investigational vaccine studies are now enrolling participants, with appointments available.",
    heroTitle: "Multiple COVID Vaccine Studies",
    heroSubtitle:
      "For Healthy Adults",
    introText: "If eligible, get paid up to $710",
    longDescription:
      "Studies are recruiting healthy adults for research involving FDA-approved COVID-19 vaccines.\n\nParticipants may be matched to one of the current COVID-19 vaccine studies. Should you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: covid50to64HealthyScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: {
      conditionalRouting: covidVaccineTrialRouting,
    },
    countryCode: "US",
    status: "inactive",
    variants: {
      "covid-vaccine-50-64-b": {
        id: "covid-vaccine-50-64-b",
        masterId: "covid-vaccine-50-64",
        status: "inactive",
        // Variant B: Streamlined landing page copy
        heroTitle: "COVID Vaccine Studies",
        heroSubtitle: "",
        introText: "If eligible, get paid up to $710",
        introTextClassName: "text-base",
        longDescription: "",
        hideProgressBarOnFirstQuestion: true,
        hideProgressBarQuestionCount: true,
      },
      "covid-vaccine-50-64-c": {
        id: "covid-vaccine-50-64-c",
        masterId: "covid-vaccine-50-64",
        status: "inactive",
        // Variant C: Simplified screener answer labels
        preScreeningQuestions: covid50to64SimplifiedScreeningQuestions,
      },
      "covid-vaccine-50-64-d": {
        id: "covid-vaccine-50-64-d",
        masterId: "covid-vaccine-50-64",
        status: "inactive",
        // Variant D: Split grouped exclusions into individual yes/no questions
        preScreeningQuestions: covid50to64HealthyScreeningQuestionsD,
      },
    },
  },

  // AZ Panda Flu Vaccine Study
  "flu-vaccine-panda-65": {
    id: "flu-vaccine-panda-65",
    template: LandingPageTemplate.EXTENDED,
    title: "Pandemic Flu Vaccine Study for Adults 65+ | HealthMatch",
    description:
      "Join a clinical trial studying investigational flu vaccines targeting pandemic influenza strains. For healthy adults 65+. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Healthy Adults 65 and Older",
    heroSubtitle:
      "Studying Investigational Pandemic Flu Vaccines",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objectives of this study are to evaluate the safety and immune response of two investigational vaccines targeting pandemic influenza strains (commonly known as the flu). Participants will be randomly assigned to receive either one of the investigational vaccines or a placebo.\n\nParticipants will receive two doses 28 days apart. Researchers will monitor safety and measure how well the vaccines stimulate protective antibody responses over time.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: azPandaScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VHEAL0C00001-65PLUS",
    countryCode: "US",
    status: "inactive",
  },

  // Novavax COVID-19 Vaccine Study (65+)
  "covid-vaccine-novavax-65": {
    id: "covid-vaccine-novavax-65",
    template: LandingPageTemplate.EXTENDED,
    title: "COVID-19 Vaccine Study for High-Risk Adults 65+ | HealthMatch",
    description:
      "Join a clinical trial studying an investigational COVID-19 vaccine for people at high risk for severe COVID-19. For adults 65+. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults 65+ at High Risk for Severe COVID-19",
    heroSubtitle:
      "Studying an Investigational COVID-19 Vaccine",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objective of this study is to evaluate the safety and immune response of an investigational COVID-19 vaccine. This study is for people who are at high risk for severe COVID-19 due to age or underlying health conditions.\n\nParticipants will receive one dose of the investigational vaccine and will be monitored for safety and immune response.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: novavaxCovid65ScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VCOVIV31865",
    countryCode: "US",
    status: "inactive",
  },

  // Novavax COVID-19 Booster Study (50-64)
  "covid-vaccine-novavax-booster": {
    id: "covid-vaccine-novavax-booster",
    template: LandingPageTemplate.EXTENDED,
    title: "COVID-19 Booster Vaccine Study for Healthy Adults 50-64 | HealthMatch",
    description:
      "Join a clinical trial studying an investigational COVID-19 booster vaccine for healthy adults aged 50-64. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Healthy Adults 50-64",
    heroSubtitle:
      "Studying an Investigational COVID-19 Booster Vaccine",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objective of this study is to evaluate the safety and immune response of an investigational COVID-19 booster vaccine. This study is for generally healthy adults aged 50-64 without chronic health conditions.\n\nParticipants will receive one dose of the investigational vaccine and will be monitored for safety and immune response.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: novavaxBoosterScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VCOVIV420",
    countryCode: "US",
    status: "testing",
  },

  // Mpox Vaccine Study (18-49)
  "mpox-vaccine": {
    id: "mpox-vaccine",
    template: LandingPageTemplate.EXTENDED,
    title: "Mpox Vaccine Study for Healthy Adults | HealthMatch",
    description:
      "Join a clinical trial studying an investigational mpox (monkeypox) vaccine for healthy adults aged 18-49. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Healthy Adults",
    heroSubtitle:
      "Studying an Investigational Mpox Vaccine",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main objective of this study is to evaluate the safety and immune response of an investigational mpox (monkeypox) vaccine. This study is for generally healthy adults without chronic health conditions.\n\nParticipants will receive the investigational vaccine and will be monitored for safety and immune response.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: mpoxVaccineScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "VMPOX046",
    countryCode: "US",
    status: "inactive",
  },

  // Norovirus (Stomach Flu) Vaccine Study (60+)
  "norovirus-vaccine": {
    id: "norovirus-vaccine",
    template: LandingPageTemplate.EXTENDED,
    title: "Investigational Norovirus (Stomach Flu) Vaccine Study | HealthMatch",
    description:
      "Join a clinical trial studying an investigational vaccine that may help prevent norovirus, which often causes vomiting and diarrhea. For adults 60+. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Older Adults",
    heroSubtitle:
      "Investigational Norovirus (Stomach Flu) Vaccine Study",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main purpose of this study is to evaluate the safety and effectiveness of an investigational vaccine that may help prevent norovirus, a contagious virus that causes stomach and intestinal illness (commonly known as \"stomach flu\").\n\nNorovirus can cause symptoms like nausea, vomiting, diarrhea, and stomach pain.\n\nParticipants will receive either the investigational vaccine or a placebo (an inactive substance). The study will monitor if the vaccine helps prevent moderate or severe symptoms of norovirus illness.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: norovirusVaccineScreeningQuestions,
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: {
      ageCohorts: [
        { minAgeInclusive: 60, maxAgeExclusive: 70, trialId: "VMODNOROP301B" },
        { minAgeInclusive: 70, maxAgeExclusive: 80, trialId: "VMODNOROP301C" },
        { minAgeInclusive: 80, maxAgeExclusive: 999, trialId: "VMODNOROP301D" },
      ],
    },
    countryCode: "US",
    status: "inactive",
  },

  // Multi-Study Vaccine Landing Page - matches patients to best available study
  "vaccine-studies": {
    id: "vaccine-studies",
    template: LandingPageTemplate.EXTENDED,
    isMultiStudy: true,
    title: "Multiple Vaccine Studies | HealthMatch",
    description:
      "Multiple vaccine studies are currently enrolling for Influenza, RSV, COVID, and more. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "Multiple Vaccine Studies",
    heroSubtitle:
      "For both healthy and higher-risk participants",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "Multiple studies are currently enrolling for new and seasonal vaccines to investigate their safety and effictiveness in preventing influenzea, RSV, COVID, and more.\n\nBased on your location and health profile, we'll match you with the best available study.\n\nMost studies offer study-related compensation for time and travel if eligible.",
    conditionId: 97, // Using healthy condition ID as it's a vaccine study
    preferredLoginMethod: LoginMethod.SMS,
    countryCode: "US",
    status: "active",

    // Common questions asked to ALL patients before zipcode
    commonPreScreeningQuestions: sharedVaccineScreeningQuestions,

    // Studies to check (in priority order)
    // ✅ Using study modules from src/features/multi-study/studies/
    // Each study is self-contained with automatic question filtering
    studies: [
      {
        studyId: seqirusFluVaccineStudy.id,
        studyName: seqirusFluVaccineStudy.name,
        trialIdToAutoApply: seqirusFluVaccineStudy.trialId,
        priority: seqirusFluVaccineStudy.priority,
        eligibleZipCodes: seqirusFluVaccineStudy.eligibleZipCodes,
        additionalQuestions: seqirusFluVaccineStudy.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: seqirusFluVaccineStudy.commonAnswerRequirements,
      },
      {
        studyId: modcoVP401Study.id,
        studyName: modcoVP401Study.name,
        trialIdToAutoApply: modcoVP401Study.trialId,
        priority: modcoVP401Study.priority,
        eligibleZipCodes: modcoVP401Study.eligibleZipCodes,
        additionalQuestions: modcoVP401Study.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: modcoVP401Study.commonAnswerRequirements,
      },
      {
        studyId: cidaraFluPreventionStudy.id,
        studyName: cidaraFluPreventionStudy.name,
        trialIdToAutoApply: cidaraFluPreventionStudy.trialId,
        priority: cidaraFluPreventionStudy.priority,
        eligibleZipCodes: cidaraFluPreventionStudy.eligibleZipCodes,
        additionalQuestions: cidaraFluPreventionStudy.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: cidaraFluPreventionStudy.commonAnswerRequirements,
      },
      {
        studyId: cidara18HighRiskStudy.id,
        studyName: cidara18HighRiskStudy.name,
        trialIdToAutoApply: cidara18HighRiskStudy.trialId,
        priority: cidara18HighRiskStudy.priority,
        eligibleZipCodes: cidara18HighRiskStudy.eligibleZipCodes,
        additionalQuestions: cidara18HighRiskStudy.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: cidara18HighRiskStudy.commonAnswerRequirements,
      },
      {
        studyId: moderna1273CovidStudy.id,
        studyName: moderna1273CovidStudy.name,
        trialIdToAutoApply: moderna1273CovidStudy.trialId,
        priority: moderna1273CovidStudy.priority,
        eligibleZipCodes: moderna1273CovidStudy.eligibleZipCodes,
        additionalQuestions: moderna1273CovidStudy.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: moderna1273CovidStudy.commonAnswerRequirements,
      },
      {
        studyId: azPandaFluStudy.id,
        studyName: azPandaFluStudy.name,
        trialIdToAutoApply: azPandaFluStudy.trialId,
        priority: azPandaFluStudy.priority,
        eligibleZipCodes: azPandaFluStudy.eligibleZipCodes,
        additionalQuestions: azPandaFluStudy.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: azPandaFluStudy.commonAnswerRequirements,
      },
      {
        studyId: novavaxCovid65Study.id,
        studyName: novavaxCovid65Study.name,
        trialIdToAutoApply: novavaxCovid65Study.trialId,
        priority: novavaxCovid65Study.priority,
        eligibleZipCodes: novavaxCovid65Study.eligibleZipCodes,
        additionalQuestions: novavaxCovid65Study.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: novavaxCovid65Study.commonAnswerRequirements,
      },
      {
        studyId: novavaxCovidBoosterStudy.id,
        studyName: novavaxCovidBoosterStudy.name,
        trialIdToAutoApply: novavaxCovidBoosterStudy.trialId,
        priority: novavaxCovidBoosterStudy.priority,
        eligibleZipCodes: novavaxCovidBoosterStudy.eligibleZipCodes,
        additionalQuestions: novavaxCovidBoosterStudy.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: novavaxCovidBoosterStudy.commonAnswerRequirements,
      },
      {
        studyId: mpoxVaccineStudy.id,
        studyName: mpoxVaccineStudy.name,
        trialIdToAutoApply: mpoxVaccineStudy.trialId,
        priority: mpoxVaccineStudy.priority,
        eligibleZipCodes: mpoxVaccineStudy.eligibleZipCodes,
        additionalQuestions: mpoxVaccineStudy.questions, // Already filtered by defineStudy()
        commonAnswerRequirements: mpoxVaccineStudy.commonAnswerRequirements,
      },
    ],
  },

  // Multi-Study ASCVD Landing Page - matches patients to the appropriate cohort
  "ascvd-studies": {
    id: "ascvd-studies",
    template: LandingPageTemplate.EXTENDED,
    isMultiStudy: true,
    title: "Cardiovascular Disease Study | HealthMatch",
    description:
      "Join a clinical trial studying an investigational medication for adults with or at high risk of atherosclerotic cardiovascular disease. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "Cardiovascular Disease Study",
    heroSubtitle:
      "For Adults With or At High Risk of ASCVD",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "Multiple studies are enrolling adults to investigate the safety and effectiveness of investigational medications for preventing major cardiovascular events.\n\nCohort A is for adults who have a history of atherosclerotic cardiovascular disease (heart attack, stroke, or peripheral artery disease).\n\nThe Amgen CVOT study is for adults with heart disease and excess weight (BMI ≥27).\n\nCohort B is for adults who are at high risk but have NOT had a heart attack or stroke yet.\n\nBased on your health profile and location, we'll match you with the most appropriate study.\n\nStudy-related compensation for time and travel may be available for eligible participants.",
    conditionId: 362, // Using cardiovascular condition ID
    preferredLoginMethod: LoginMethod.SMS,
    countryCode: "US",
    status: "active",

    // Common questions asked to ALL patients before study-specific questions
    commonPreScreeningQuestions: sharedASCVDScreeningQuestions,

    // Studies to check (in priority order)
    // Priority 1: Established ASCVD (Cohort A)
    // Priority 2: Amgen CVOT (with specific zip codes and BMI requirement)
    // Priority 3: High Risk (Cohort B)
    studies: [
      {
        studyId: ascvdEstablishedStudy.id,
        studyName: ascvdEstablishedStudy.name,
        trialIdToAutoApply: ascvdEstablishedStudy.trialId,
        priority: ascvdEstablishedStudy.priority,
        eligibleZipCodes: ascvdEstablishedStudy.eligibleZipCodes,
        additionalQuestions: ascvdEstablishedStudy.questions, // Already filtered by defineStudy()
      },
      {
        studyId: amgenCVOTStudy.id,
        studyName: amgenCVOTStudy.name,
        trialIdToAutoApply: amgenCVOTStudy.trialId,
        priority: amgenCVOTStudy.priority,
        eligibleZipCodes: amgenCVOTStudy.eligibleZipCodes,
        additionalQuestions: amgenCVOTStudy.questions, // Already filtered by defineStudy()
      },
      {
        studyId: ascvdHighRiskStudy.id,
        studyName: ascvdHighRiskStudy.name,
        trialIdToAutoApply: ascvdHighRiskStudy.trialId,
        priority: ascvdHighRiskStudy.priority,
        eligibleZipCodes: ascvdHighRiskStudy.eligibleZipCodes,
        additionalQuestions: ascvdHighRiskStudy.questions, // Already filtered by defineStudy()
      },
    ],
  },

  // ASCVD Established Cardiovascular Disease Study - Cohort A
  "ascvd-established": {
    id: "ascvd-established",
    template: LandingPageTemplate.EXTENDED,
    title: "Cardiovascular Disease Study - Established ASCVD | HealthMatch",
    description:
      "Join a clinical trial studying an investigational medication for adults with a history of atherosclerotic cardiovascular disease such as heart attacks or strokes. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults with a History of Cardiovascular Disease",
    heroSubtitle:
      "Studying an Investigational Medication for Preventing Major Cardiovascular Events",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main purpose of this study is to understand the safety and effectiveness of an investigational medication in adults who have a history of atherosclerotic cardiovascular disease (ASCVD) —such as heart attacks or strokes.\n\nParticipants are assigned to receive either the investigational medication or a placebo, in addition to their current cholesterol-lowering therapy.\n\nResearchers aim to evaluate if the investigational medication helps reduce the risk of major heart-related complications including heart attack, stroke, or death from cardiovascular causes.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: azascvdEstablishedScreeningQuestions,
    conditionId: 362, // Using cardiovascular condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "DMASCVDAZD012A",
    countryCode: "US",
    status: "active",
  },

  // ASCVD High Risk First Event Study - Cohort B
  "ascvd-high-risk": {
    id: "ascvd-high-risk",
    template: LandingPageTemplate.EXTENDED,
    title: "Cardiovascular Disease Prevention Study - High Risk | HealthMatch",
    description:
      "Join a clinical trial studying an investigational medication for adults who are at high risk of atherosclerotic cardiovascular disease such as heart attacks or strokes. Study-related compensation for time and travel may be available if eligible.",
    heroTitle: "For Adults at High Risk of Cardiovascular Disease",
    heroSubtitle:
      "Studying an Investigational Medication for Preventing Major Cardiovascular Events",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "The main purpose of this study is to understand the safety and effectiveness of an investigational medication in adults who are at high risk of atherosclerotic cardiovascular disease (ASCVD) —such as heart attacks or strokes.\n\nParticipants are assigned to receive either the investigational medication or a placebo, in addition to their current cholesterol-lowering therapy.\n\nResearchers aim to evaluate if the investigational medication helps reduce the risk of major heart-related complications including heart attack, stroke, or death from cardiovascular causes.\n\nShould you express interest, the research site will contact you directly to provide further details and address any questions you may have about study requirements, risks/benefits, and compensation.",
    preScreeningQuestions: azascvdHighRiskScreeningQuestions,
    conditionId: 362, // Using cardiovascular condition ID
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "DMASCVDAZD012B",
    countryCode: "US",
    status: "active",
  },

  "alnylam-htn": {
    id: "alnylam-htn",
    template: LandingPageTemplate.EXTENDED,
    title: "Hypertension Research Study | HealthMatch",
    description:
      "Adults with high blood pressure may qualify for an investigational treatment study. Compensation may be available.",
    heroTitle: "For Adults Living with High Blood Pressure",
    heroSubtitle: "Help researchers explore a new investigational treatment for hypertension.",
    introText: "Compensation may be available for eligible participants.",
    longDescription:
      "Even when you take your medication, blood pressure can be hard to keep in a healthy range. Researchers are studying an investigational treatment for adults with hypertension. After a quick eligibility check, we'll ask you to share a few recent blood pressure readings so the study team can confirm whether you're a fit.",
    preScreeningQuestions: alnylamHypertensionScreeningQuestions,
    conditionId: 132, // high blood pressure (hypertension). Was 177 — that's the
    // HAS_HYPERTENSION *attribute* id, not a condition id, which 500'd /refer-patient.
    countryCode: "US",
    // "testing" keeps this reachable by direct URL (/alnylam-htn) but hidden
    // from the Home listing (useActiveTrials only shows "active"). Flip to
    // "active" to launch the BP-upload study publicly.
    status: "testing",
    preferredLoginMethod: LoginMethod.SMS,
    thankYouPage: {
      bodyText:
        "Thanks — your application is in. The study team will reach out shortly. While you're here, you can help speed things up by sharing a few recent blood pressure readings below.",
    },
    bpUpload: {
      headline: "Help us match you faster — share a few BP readings",
      bodyText:
        "Your application is already submitted. Sharing one or two recent readings just helps the study team confirm you're a fit, sooner.",
      instructionTitle: "Best readings to share",
      instructionText:
        "A morning reading, before you take your blood pressure medication, after sitting calmly for 5 minutes. Multiple days is even better.",
      ctaLabel: "Send my readings",
      laterCtaLabel: "I'll do this later",
      successHeadline: "Got it. Thank you.",
      successBody:
        "Your readings are with the study team. We'll be in touch soon — no further action needed for now.",
      maxImages: 5,
    },
  },

};


// Development-only test configuration - simple example for backward compatibility
// More comprehensive development examples now live in developmentLandingPageConfigs.ts
export const developmentConfigs: Record<string, LandingPageWithVariants | MultiStudyLandingPageConfig> = {
  "test-landing": developmentLandingPageConfigs["test-landing"],
};

// Combine configurations based on environment
export const landingPageConfigs: Record<string, LandingPageWithVariants | MultiStudyLandingPageConfig> = {
  ...productionConfigs,
  ...(isDev ? developmentLandingPageConfigs : {}),
};

/**
 * Get a landing page configuration by ID
 * @param id The ID of the landing page configuration to retrieve
 * @returns The landing page configuration or undefined if not found
 */
export function getLandingPageConfig(
  id: string,
): LandingPageConfig | undefined {
  // First check if this is a direct master config
  const masterConfig = landingPageConfigs[id];
  if (masterConfig) {
    return masterConfig;
  }

  // If not found, look for it in variants (only for LandingPageWithVariants)
  for (const config of Object.values(landingPageConfigs)) {
    // Skip multi-study configs which don't have variants
    if ("isMultiStudy" in config && config.isMultiStudy) {
      continue;
    }

    const variantsConfig = config as LandingPageWithVariants;
    const variant = variantsConfig.variants?.[id];
    if (variant) {
      // Merge the variant with its master config, excluding the variants field
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { variants, ...masterWithoutVariants } = variantsConfig;
      return {
        ...masterWithoutVariants,
        ...variant,
      };
    }
  }

  return undefined;
}

/**
 * Parse URL parameters to extract landing page configuration
 * This allows for dynamic configuration via URL parameters
 * @param params URL search parameters
 * @returns A landing page configuration based on URL parameters
 */
export function getLandingPageConfigFromParams(
  params: URLSearchParams,
): LandingPageConfig | undefined {
  // Check if this is a variant of an existing config
  const masterId = params.get("masterId");
  if (masterId) {
    const masterConfig = landingPageConfigs[masterId];
    if (masterConfig) {
      // Check if this is a multi-study config (doesn't support URL param variants)
      if ("isMultiStudy" in masterConfig && masterConfig.isMultiStudy) {
        return masterConfig;
      }

      // Handle regular configs with variants
      const variantsConfig = masterConfig as LandingPageWithVariants;
      // Start with the master config (excluding variants) and override with URL parameters
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { variants, ...masterWithoutVariants } = variantsConfig;
      return {
        ...masterWithoutVariants,
        id: params.get("id") || "dynamic",
        title: params.get("title") || masterConfig.title,
        description: params.get("description") || masterConfig.description,
        heroTitle: params.get("heroTitle") || masterConfig.heroTitle,
        heroSubtitle: params.get("heroSubtitle") || masterConfig.heroSubtitle,
      };
    }
  }

  return undefined;
}

export function getAllLandingPageIds(): string[] {
  return Object.keys(landingPageConfigs);
}
